import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Divider, FormControlLabel, Grid, MenuItem, Paper, Stack, TextField, Typography
} from '@mui/material';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import PublishIcon from '@mui/icons-material/Publish';
import {
    createOperationalAssignment,
    createOperationalProfile,
    getEffectiveOperationalConfiguration,
    getOperationalAssignments,
    getOperationalTerminalStatus,
    getOperationalProfiles,
    publishOperationalProfileVersion
} from '../services/operationalConfigService';

const defaultConfiguration = {
    schema_version: 1,
    operations: {
        sale: true, refund: true, void: true, pre_auth: true, post_auth: true,
        cashback: false, manual: false, fallback: true, chip: true,
        contactless: true, mag_stripe: true
    },
    tip: {
        enabled: false, suggested_percentages: [10, 15, 20], allow_custom: true,
        maximum_percent: 30, maximum_amount_minor: 0
    },
    msi: { enabled: false, allowed_terms: [3, 6, 9, 12], minimum_amount_by_term: {} },
    qps: { enabled: false, amount_limit_minor: 0 },
    risk: {
        contactless_transaction_limit_minor: 0,
        contactless_cvm_limit_minor: 0,
        contact_cvm_limit_minor: 0
    },
    receipt: {
        auto_print_merchant_copy: true, offer_customer_copy: true,
        result_display_seconds: 3
    }
};

const moneyToMinor = (value) => Math.round((Number(value) || 0) * 100);
const minorToMoney = (value) => ((Number(value) || 0) / 100).toFixed(2);

const BooleanField = ({ label, checked, onChange }) => (
    <FormControlLabel control={<Checkbox checked={checked} onChange={(event) => onChange(event.target.checked)} />} label={label} />
);

const OperationalConfiguration = () => {
    const [profiles, setProfiles] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [configuration, setConfiguration] = useState(defaultConfiguration);
    const [assignment, setAssignment] = useState({ profile_version_id: '', scope_type: 'GLOBAL', scope_id: '', priority: 0 });
    const [previewSerial, setPreviewSerial] = useState('');
    const [preview, setPreview] = useState(null);
    const [terminalStatus, setTerminalStatus] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [profileData, assignmentData, terminalData] = await Promise.all([
                getOperationalProfiles(), getOperationalAssignments(), getOperationalTerminalStatus()
            ]);
            setProfiles(profileData.items || []);
            setAssignments(assignmentData.items || []);
            setTerminalStatus(terminalData.items || []);
        } catch (error) {
            setMessage({ severity: 'error', text: 'No fue posible cargar la configuración operativa.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const publishedProfiles = useMemo(() => profiles.filter((profile) => profile.latest_status === 'PUBLISHED'), [profiles]);
    const updateSection = (section, key, value) => setConfiguration((current) => ({
        ...current,
        [section]: { ...current[section], [key]: value }
    }));

    const createProfile = async () => {
        setSaving(true); setMessage(null);
        try {
            await createOperationalProfile({ name, description, configuration });
            setName(''); setDescription('');
            setMessage({ severity: 'success', text: 'Perfil creado como borrador. Revísalo y publícalo antes de asignarlo.' });
            await load();
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'No fue posible crear el perfil.' });
        } finally { setSaving(false); }
    };

    const publish = async (versionId) => {
        setSaving(true); setMessage(null);
        try {
            await publishOperationalProfileVersion(versionId);
            setMessage({ severity: 'success', text: 'Versión publicada. Ya puede asignarse.' });
            await load();
        } catch (error) {
            setMessage({ severity: 'error', text: 'No fue posible publicar la versión.' });
        } finally { setSaving(false); }
    };

    const assign = async () => {
        setSaving(true); setMessage(null);
        try {
            await createOperationalAssignment({
                ...assignment,
                profile_version_id: Number(assignment.profile_version_id),
                scope_id: assignment.scope_type === 'GLOBAL' ? '' : assignment.scope_id,
                priority: Number(assignment.priority)
            });
            setMessage({ severity: 'success', text: 'Perfil asignado correctamente.' });
            await load();
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'No fue posible crear la asignación.' });
        } finally { setSaving(false); }
    };

    const previewEffective = async () => {
        setSaving(true); setMessage(null);
        try { setPreview(await getEffectiveOperationalConfiguration(previewSerial)); }
        catch (error) { setMessage({ severity: 'error', text: 'No fue posible calcular la configuración de esa terminal.' }); }
        finally { setSaving(false); }
    };


    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h4" component="h1">Operativas y configuración</Typography>
                <Typography color="text.secondary">Perfiles versionados para propina, MSI, QPS, límites y capacidades comerciales.</Typography>
            </Box>
            {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <SettingsSuggestIcon color="primary" /><Typography variant="h6">Nuevo perfil</Typography>
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}><TextField fullWidth label="Nombre" value={name} onChange={(e) => setName(e.target.value)} /></Grid>
                    <Grid item xs={12} md={7}><TextField fullWidth label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} /></Grid>
                    <Grid item xs={12}><Divider><Typography variant="overline">Operativas</Typography></Divider></Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(configuration.operations).map(([key, value]) => (
                                <BooleanField key={key} label={key.replaceAll('_', ' ')} checked={value} onChange={(checked) => updateSection('operations', key, checked)} />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1">Propina</Typography>
                        <BooleanField label="Habilitada" checked={configuration.tip.enabled} onChange={(value) => updateSection('tip', 'enabled', value)} />
                        <BooleanField label="Monto personalizado" checked={configuration.tip.allow_custom} onChange={(value) => updateSection('tip', 'allow_custom', value)} />
                        <TextField fullWidth margin="dense" label="Porcentajes sugeridos" value={configuration.tip.suggested_percentages.join(',')}
                            onChange={(e) => updateSection('tip', 'suggested_percentages', e.target.value.split(',').map((v) => Number(v.trim())).filter(Number.isFinite))} />
                        <TextField fullWidth margin="dense" type="number" label="Porcentaje máximo" value={configuration.tip.maximum_percent}
                            onChange={(e) => updateSection('tip', 'maximum_percent', Number(e.target.value))} />
                        <TextField fullWidth margin="dense" type="number" label="Monto máximo (MXN)" value={minorToMoney(configuration.tip.maximum_amount_minor)}
                            onChange={(e) => updateSection('tip', 'maximum_amount_minor', moneyToMinor(e.target.value))} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1">MSI</Typography>
                        <BooleanField label="Habilitado" checked={configuration.msi.enabled} onChange={(value) => updateSection('msi', 'enabled', value)} />
                        <TextField fullWidth margin="dense" label="Plazos permitidos" value={configuration.msi.allowed_terms.join(',')}
                            onChange={(e) => updateSection('msi', 'allowed_terms', e.target.value.split(',').map((v) => Number(v.trim())).filter(Number.isFinite))} />
                        <Typography variant="caption" color="text.secondary">Los mínimos por plazo se incorporarán en el editor avanzado.</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1">QPS y CVM</Typography>
                        <BooleanField label="QPS habilitado" checked={configuration.qps.enabled} onChange={(value) => updateSection('qps', 'enabled', value)} />
                        <TextField fullWidth margin="dense" type="number" label="Límite QPS (MXN)" value={minorToMoney(configuration.qps.amount_limit_minor)}
                            onChange={(e) => updateSection('qps', 'amount_limit_minor', moneyToMinor(e.target.value))} />
                        <TextField fullWidth margin="dense" type="number" label="Límite contactless (MXN)" value={minorToMoney(configuration.risk.contactless_transaction_limit_minor)}
                            onChange={(e) => updateSection('risk', 'contactless_transaction_limit_minor', moneyToMinor(e.target.value))} />
                        <TextField fullWidth margin="dense" type="number" label="CVM contactless (MXN)" value={minorToMoney(configuration.risk.contactless_cvm_limit_minor)}
                            onChange={(e) => updateSection('risk', 'contactless_cvm_limit_minor', moneyToMinor(e.target.value))} />
                        <TextField fullWidth margin="dense" type="number" label="CVM contacto (MXN)" value={minorToMoney(configuration.risk.contact_cvm_limit_minor)}
                            onChange={(e) => updateSection('risk', 'contact_cvm_limit_minor', moneyToMinor(e.target.value))} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <BooleanField label="Imprimir copia comercio automáticamente" checked={configuration.receipt.auto_print_merchant_copy} onChange={(value) => updateSection('receipt', 'auto_print_merchant_copy', value)} />
                        <BooleanField label="Ofrecer copia cliente" checked={configuration.receipt.offer_customer_copy} onChange={(value) => updateSection('receipt', 'offer_customer_copy', value)} />
                    </Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Segundos de resultado" value={configuration.receipt.result_display_seconds} onChange={(e) => updateSection('receipt', 'result_display_seconds', Number(e.target.value))} /></Grid>
                    <Grid item xs={12} md={3}><Button fullWidth sx={{ height: 56 }} variant="contained" disabled={saving || !name.trim()} onClick={createProfile}>Crear borrador</Button></Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2}>
                {profiles.map((profile) => (
                    <Grid item xs={12} md={6} lg={4} key={profile.id}>
                        <Card variant="outlined"><CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">{profile.name}</Typography>
                                <Chip size="small" color={profile.latest_status === 'PUBLISHED' ? 'success' : 'warning'} label={`v${profile.latest_version} · ${profile.latest_status}`} />
                            </Stack>
                            <Typography color="text.secondary" sx={{ minHeight: 48, mt: 1 }}>{profile.description || 'Sin descripción'}</Typography>
                            {profile.latest_status === 'DRAFT' && <Button startIcon={<PublishIcon />} disabled={saving} onClick={() => publish(profile.latest_version_id)}>Publicar</Button>}
                        </CardContent></Card>
                    </Grid>
                ))}
            </Grid>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Asignar versión publicada</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}><TextField select fullWidth label="Perfil" value={assignment.profile_version_id} onChange={(e) => setAssignment({ ...assignment, profile_version_id: e.target.value })}>
                        {publishedProfiles.map((profile) => <MenuItem key={profile.latest_version_id} value={profile.latest_version_id}>{profile.name} · v{profile.latest_version}</MenuItem>)}
                    </TextField></Grid>
                    <Grid item xs={12} md={3}><TextField select fullWidth label="Alcance" value={assignment.scope_type} onChange={(e) => setAssignment({ ...assignment, scope_type: e.target.value, scope_id: '' })}>
                        {['GLOBAL', 'AFFILIATION', 'MERCHANT', 'DEVICE'].map((scope) => <MenuItem key={scope} value={scope}>{scope}</MenuItem>)}
                    </TextField></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth disabled={assignment.scope_type === 'GLOBAL'} label="ID o número de serie" value={assignment.scope_id} onChange={(e) => setAssignment({ ...assignment, scope_id: e.target.value })} /></Grid>
                    <Grid item xs={12} md={2}><Button fullWidth variant="contained" disabled={saving || !assignment.profile_version_id || (assignment.scope_type !== 'GLOBAL' && !assignment.scope_id)} onClick={assign}>Asignar</Button></Grid>
                </Grid>
                <Stack spacing={1} sx={{ mt: 2 }}>
                    {assignments.map((item) => <Box key={item.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><Chip size="small" label={item.scope_type} /><Typography>{item.profile_name} v{item.version} → {item.scope_id || 'Todas las terminales'}</Typography></Box>)}
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">Vista previa por terminal</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                    <TextField fullWidth label="Número de serie" value={previewSerial} onChange={(e) => setPreviewSerial(e.target.value)} />
                    <Button variant="outlined" disabled={saving || !previewSerial.trim()} onClick={previewEffective}>Calcular configuración efectiva</Button>
                </Stack>
                {preview && <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', mt: 2 }}>{JSON.stringify(preview, null, 2)}</Box>}
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Estado de terminales</Typography>
                <Stack spacing={1}>
                    {terminalStatus.map((terminal) => {
                        const synchronized = terminal.desired_hash && terminal.desired_hash === terminal.applied_hash;
                        return <Box key={terminal.serial_number} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, flexWrap: 'wrap' }}>
                            <Chip size="small" color={synchronized ? 'success' : terminal.last_status === 'REJECTED' ? 'error' : 'warning'} label={synchronized ? 'Sincronizada' : terminal.last_status || 'Sin reporte'} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">{terminal.serial_number}</Typography>
                                <Typography variant="caption" color="text.secondary">{terminal.terminal_model_id ? `${terminal.manufacturer} ${terminal.model} ${terminal.model_version}` : 'Sin modelo asignado'}</Typography>
                                {terminal.last_error && <Typography variant="caption" color="error" display="block">{terminal.last_error}</Typography>}
                            </Box>
                        </Box>;
                    })}
                    {terminalStatus.length === 0 && <Typography color="text.secondary">No hay terminales registradas.</Typography>}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default OperationalConfiguration;
